export const DIGITAL_HUMAN_VERIFICATION_STATUS = {
  UNVERIFIED: 0,
  VERIFYING: 1,
  VERIFIED: 2,
  VERIFICATION_FAILED: 3,
} as const;

export const FACE_VERIFICATION_SESSION_STATE = {
  PENDING: 0,
  VERIFYING: 1,
  SUCCESS: 2,
  FAILED: 3,
  EXPIRED: 4,
} as const;

export const PRIMARY_ASSET_VERIFICATION_STATUS = {
  NOT_STARTED: 0,
  PROCESSING: 1,
  VERIFIED: 2,
  FAILED: 3,
} as const;

export const DIGITAL_HUMAN_GENDER = {
  MALE: 1,
  FEMALE: 2,
  OTHER: 3,
} as const;

export const DIGITAL_HUMAN_VERIFICATION_STAGE = {
  UNVERIFIED: 'unverified',
  BIO_VERIFYING: 'bioVerifying',
  BIO_FAILED: 'bioFailed',
  PRIMARY_IMAGE_REQUIRED: 'primaryImageRequired',
  PRIMARY_IMAGE_VERIFYING: 'primaryImageVerifying',
  PRIMARY_IMAGE_FAILED: 'primaryImageFailed',
  VERIFIED: 'verified',
} as const;

export const DIGITAL_HUMAN_AVAILABILITY_SCOPE = {
  ONLY_ORIGIN_PROJECT: 1,
  SELECTED_PROJECTS: 2,
  ALL_ORG_PROJECTS: 3,
} as const;

export type DigitalHumanVerificationStatus =
  (typeof DIGITAL_HUMAN_VERIFICATION_STATUS)[keyof typeof DIGITAL_HUMAN_VERIFICATION_STATUS];

export type FaceVerificationSessionState =
  (typeof FACE_VERIFICATION_SESSION_STATE)[keyof typeof FACE_VERIFICATION_SESSION_STATE];

export type PrimaryAssetVerificationStatus =
  (typeof PRIMARY_ASSET_VERIFICATION_STATUS)[keyof typeof PRIMARY_ASSET_VERIFICATION_STATUS];

export type DigitalHumanGender = (typeof DIGITAL_HUMAN_GENDER)[keyof typeof DIGITAL_HUMAN_GENDER];

export type DigitalHumanVerificationStage =
  (typeof DIGITAL_HUMAN_VERIFICATION_STAGE)[keyof typeof DIGITAL_HUMAN_VERIFICATION_STAGE];
export type DigitalHumanAvailabilityScope =
  (typeof DIGITAL_HUMAN_AVAILABILITY_SCOPE)[keyof typeof DIGITAL_HUMAN_AVAILABILITY_SCOPE];

export type DigitalHumanVerificationStep =
  | 'bio_verification_required'
  | 'bio_verification_processing'
  | 'bio_verification_failed'
  | 'primary_image_required'
  | 'primary_image_processing'
  | 'primary_image_failed'
  | 'verification_completed'
  | 'unknown'
  | string;

export interface DigitalHuman {
  digitalHumanId: string;
  name: string;
  description?: string | null;
  primaryImageUrl?: string | null;
  gender?: DigitalHumanGender | null;
  verificationStatus: DigitalHumanVerificationStatus;
  verificationStage: DigitalHumanVerificationStage;
  verifiedSessionId?: string | null;
  verifiedAt?: string | null;
  primaryAssetId?: string | null;
  providerGroupId?: string | null;
  primaryAssetVerificationStatus: PrimaryAssetVerificationStatus;
  primaryAssetFailReason?: string | null;
  primaryAssetVerifiedAt?: string | null;
  creditsUsed: number;
  isProjectRestricted: boolean;
  availabilityScope: DigitalHumanAvailabilityScope;
  originProjectId?: string | null;
  sourceProjectId?: string | null;
  boundProjectIds: string[];
  verificationCreditsRequired: number;
  verificationCreditNotice?: string | null;
  userId: string;
  orgType?: number | null;
  orgId?: string | null;
  metaData: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DigitalHumanListParams {
  page?: number;
  pageSize?: number;
  verificationStatus?: DigitalHumanVerificationStatus;
  verificationStage?: DigitalHumanVerificationStage;
  projectId?: string;
  search?: string;
}

export interface DigitalHumanListResponse {
  items: DigitalHuman[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DigitalHumanCreateRequest {
  name: string;
  description?: string | null;
  primaryImageUrl?: string | null;
  gender?: DigitalHumanGender | null;
  projectId?: string | null;
  isProjectRestricted?: boolean;
  metaData?: Record<string, unknown>;
}

export interface DigitalHumanCreateFormData {
  name: string;
  description?: string | null;
  primaryImageFile: File;
  gender?: DigitalHumanGender | null;
  projectId?: string | null;
  isProjectRestricted?: boolean;
}

export interface DigitalHumanUpdateRequest {
  name?: string;
  description?: string | null;
  primaryImageUrl?: string | null;
  gender?: DigitalHumanGender | null;
  isProjectRestricted?: boolean;
  availabilityScope?: DigitalHumanAvailabilityScope;
  boundProjectIds?: string[];
  metaData?: Record<string, unknown>;
}

export interface DigitalHumanUpdateFormData {
  name?: string;
  description?: string | null;
  primaryImageFile?: File;
  gender?: DigitalHumanGender | null;
  isProjectRestricted?: boolean;
  availabilityScope?: DigitalHumanAvailabilityScope;
  boundProjectIds?: string[];
}

export interface DigitalHumanVerificationRequest {
  projectName?: string;
  callbackUrl?: string;
  projectId?: string;
}

export interface DigitalHumanVerificationCreditPolicy {
  creditsRequired: number;
  firstVerificationNotice: string;
  retryNotice: string;
}

export interface DigitalHumanVerificationResponse {
  digitalHumanId: string;
  verificationStatus: DigitalHumanVerificationStatus;
  primaryAssetVerificationStatus: PrimaryAssetVerificationStatus;
  primaryAssetFailReason?: string | null;
  creditsUsed: number;
  creditsCharged: number;
  verificationCreditsRequired: number;
  creditNotice?: string | null;
  step: DigitalHumanVerificationStep;
  sessionId?: string | null;
  sessionState?: FaceVerificationSessionState | null;
  h5Url?: string | null;
  h5QrCodeDataUrl?: string | null;
  expiresAt?: string | null;
  isExpired?: boolean;
  h5Scanned?: boolean;
  h5ScannedAt?: string | null;
}

export interface DigitalHumanVerificationStatusResponse {
  digitalHumanId: string;
  verificationStatus: DigitalHumanVerificationStatus;
  primaryAssetVerificationStatus: PrimaryAssetVerificationStatus;
  primaryAssetFailReason?: string | null;
  step: DigitalHumanVerificationStep;
  sessionId?: string | null;
  sessionState?: FaceVerificationSessionState | null;
  h5Scanned: boolean;
  h5ScannedAt?: string | null;
  expiresAt?: string | null;
  isExpired: boolean;
  bioVerifiedAt?: string | null;
  assetGroupId?: string | null;
  primaryAssetId?: string | null;
}
